import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Polverini Hair System di Leone Itria";
const TITLE_SUFFIX = "Polverini Hair System Palazzolo Acreide";
const HOME_TITLE = "Polverini Hair System di Leone Itria - Parrucchiere Palazzolo Acreide";
const DEFAULT_DESC =
  "Polverini Hair System di Itria Leone, parrucchiere ed estetica a Palazzolo Acreide (SR) in Via Bando Superiore 49: taglio Sculpture, colore biologico, unghie, pedicure, epilazione.";
const DEFAULT_OG = "https://parrucchierepalazzolo.com/assets/hero/salone.jpg";
const BASE_URL = "https://parrucchierepalazzolo.com";

/**
 * SEO meta tags helper.
 *
 * Con React 19 react-helmet-async rende <title>/<meta>/<link> come elementi React
 * "hoistati" in <head>: i tag statici di public/index.html NON vengono sostituiti
 * ma affiancati (title/description/og:* doppi). I tag statici sono quindi marcati
 * con data-seo-fallback (servono solo ai crawler senza JS) e rimossi qui al mount,
 * così nel DOM resta un solo title, una sola description, un solo canonical.
 *
 * Props:
 *  - title: nome pagina; diventa "<Pagina> · Polverini Hair System Palazzolo Acreide"
 *           (vuoto = home: HOME_TITLE)
 *  - description: meta description (140-160 caratteri)
 *  - image: og:image URL (absolute)
 *  - path: pathname for canonical (es. "/staff")
 *  - type: og:type ("website" | "article")
 *  - article: { publishedTime, modifiedTime, author }
 *  - noindex: rotte tecniche/admin → robots noindex,nofollow e nessun canonical
 */
export default function SEO({
  title,
  description,
  image = DEFAULT_OG,
  path = "/",
  type = "website",
  article,
  noindex = false,
}) {
  useEffect(() => {
    document.querySelectorAll("head [data-seo-fallback]").forEach((el) => el.remove());
  }, []);

  const fullTitle = title ? `${title} · ${TITLE_SUFFIX}` : HOME_TITLE;
  const desc = description || DEFAULT_DESC;
  const url = `${BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const ogImage = image.startsWith("http") ? image : `${BASE_URL}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />
      {!noindex && <link rel="canonical" href={url} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="it_IT" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article */}
      {type === "article" && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {type === "article" && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {type === "article" && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
    </Helmet>
  );
}
